import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { api } from '../api'
import { LoadingBlock, Empty } from '../components/States'
import { useAsync } from '../lib/useAsync'
import { plain } from '../lib/plain'
import { money } from '../lib/money'
import { BenchmarkFeedback } from '../components/BenchmarkFeedback'
import { canDisagree, formWords } from '../lib/feedback-view'
import { NOT_PRICED, closestOf, closestLine } from '../lib/not-priced'
import { scaleFor } from '../lib/scale'
import { howSure } from '../lib/howSure'
import { WHY_ORDER, HOW_ORDER } from '../types'
import type { Benchmark, BenchmarkCut, MarketBand } from '../types'
import { BackButton } from '../components/BackButton'

/**
 * One role, its shortlist, and the band that comes out of it.
 *
 * Two views over the same benchmark, because they are two jobs. **Matches** is
 * where a person decides which survey cuts describe this role. **Result** is
 * the answer that falls out of those decisions. The model proposes; the person
 * confirms; the arithmetic follows.
 *
 * The shortlist is everything that reached the model, not only what it picked.
 * Showing only the picks meant ticking one more was a decision made blind, and
 * the only way to reconsider a rejected cut was to run the whole thing again.
 *
 * **What was left out is not clutter.** It is what turns a number into
 * something an HR director will defend in a meeting, and it is the fastest way
 * to see that a survey was mapped wrong when it was uploaded: thirty-one cuts
 * excluded as hourly, in a survey of salaried roles, is a mapping mistake and
 * not a data problem.
 */
export function BenchmarkDetail() {
  const { jdId = '' } = useParams()
  const navigate = useNavigate()
  const { data, error, loading, reload } = useAsync<Benchmark>(() => api.getBenchmark(jdId), [jdId])

  const [ticked, setTicked] = useState<string[] | null>(null)
  const [leaning, setLeaning] = useState<Record<string, number>>({})
  const [view, setView] = useState<'result' | 'matches' | null>(null)
  // The Disagree with this match form, opened from the Result card (STE-992).
  const [disagreeing, setDisagreeing] = useState(false)
  // A closest job picked with Suggest this job, on a role that was not priced.
  const [suggested, setSuggested] = useState('')
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)

  if (loading) return <LoadingBlock label="Reading this benchmark" />
  if (error) {
    return (
      <Empty title="This role has not been benchmarked yet" action={{ label: 'Back to benchmarking', onClick: () => navigate('/benchmarking') }}>
        <p>Run benchmarking and it will be matched against your survey.</p>
      </Empty>
    )
  }
  if (!data) return null

  const profile = data.profile ?? null
  const shortlist = [...data.cuts].sort((a, b) => (b.score ?? 0) - (a.score ?? 0))
  const chosen = ticked ?? shortlist.filter(isChosen).map((c) => c.code)
  const emphasisOf = (cut: BenchmarkCut) => leaning[cut.code] ?? cut.emphasis ?? 1
  const changed = ticked !== null || Object.keys(leaning).length > 0

  /*
   * Which view opens first.
   *
   * An answer that exists opens on the answer. A role with nothing to show
   * opens on the shortlist, because the useful thing to do there is pick
   * something rather than read that there is nothing.
   */
  const showing = view ?? (data.band ? 'result' : 'matches')

  async function run() {
    setSaving(true)
    setSaveError(null)
    try {
      /*
       * Every ticked cut's weight, including the ones sitting at 1.
       *
       * This used to send only the weights that were not 1, being the
       * default. The route then fell back to what was already stored for a
       * code it heard nothing about, so turning a cut from twice back to once
       * said nothing about it and it stayed at twice. A stepper that moves on
       * screen and does not move the answer is worse than no stepper.
       */
      const weights: Record<string, number> = {}
      for (const code of chosen) {
        const cut = shortlist.find((c) => c.code === code)
        weights[code] = cut ? emphasisOf(cut) : 1
      }
      await api.saveBenchmarkCuts(jdId, chosen, weights)
      setTicked(null)
      setLeaning({})
      setView('result')
      reload()
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'That did not save.')
    } finally {
      setSaving(false)
    }
  }

  const ownRange = profile ? range(profile.payMin, profile.payMax) : null

  const header = (
    <header className="title-row bench-head">
      <div>
        <BackButton to="benchmarking" onClick={() => navigate('/benchmarking')} />
        <h1 className="page">{data.jdTitle || profile?.title || 'This role'}</h1>
        <p className="subline">
        {[
          profile?.family,
          profile?.level,
          profile?.location,
          ownRange ? `current range ${ownRange}` : '',
          data.surveyName
            ? `${data.surveyName}${data.surveyFiles && data.surveyFiles > 1 ? `, ${data.surveyFiles} uploads` : ''}`
            : '',
        ].filter(Boolean).join(' · ')}
      </p>
        {/*
          An unresolved location is said out loud, not defaulted away.

          This used to read "the United States" whenever no country was
          stored, which is exactly what it says when the role's location was
          not understood. A role written "India (Hyderabad)" was priced
          against American rows and this line called it the United States, so
          the one place that could have shown the mistake confirmed it
          instead.
        */}
        {data.country ? (
          <p className="hint">
            Matched on job family, then level, then where the role sits. All cuts cover {data.country}.
          </p>
        ) : (
          <p className="hint">
            Matched on job family and then level. <strong>This role's location did not name a country we hold
            market data for</strong>, so every country in the survey was considered and the figures may mix them.
            Setting the role's location to a country will narrow it.
          </p>
        )}
        {data.outOfDate && (
          <p className="pill pill--stale" role="status">
            This role has been edited since the benchmark ran. Run it again for a current answer.
          </p>
        )}
      </div>
    </header>
  )

  /*
   * Broke, rather than found nothing. Two different answers that used to look
   * like one, because a failure saved nothing and the role read "never run".
   */
  if (data.failedReason) {
    return (
      <section className="screen">
        {header}
        <section className="card card--pad">
          <h2>This role could not be benchmarked</h2>
          <p className="subline prose">
            Something went wrong while matching it. Nothing has been saved against it, and
            running it again is safe.
          </p>
          <pre className="failure">{data.failedReason}</pre>
          <div className="actions">
            <button type="button" className="btn btn--primary" onClick={() => navigate('/benchmarking')}>
              Back to benchmarking
            </button>
          </div>
        </section>
      </section>
    )
  }

  /*
   * Not priced is an answer, and it gets said plainly rather than shown as an
   * empty result page.
   *
   * **It said "No close match in this survey" and then listed the jobs that
   * came closest**, and the list said "no match found". A customer reasonably
   * asked which was true (STE-1063). So it says what happened: the role was
   * compared, and none was close enough to price it from.
   *
   * And it can be disagreed with, here, because this is the role HR most often
   * disagrees with. A job HR suggests goes to the matcher as a suggestion; it
   * is never paired with the role by hand.
   */
  if (data.noMatchReason !== null && shortlist.length === 0) {
    const pointer = closestLine(data)
    return (
      <section className="screen">
        {header}
        <section className="card card--pad">
          <h2>{NOT_PRICED.heading}</h2>
          {pointer && <p className="prose not-priced__lead">{pointer}</p>}
          <p className="prose">{plain(data.noMatchReason)}</p>
          <p className="subline prose">
            It has been left without a figure rather than matched to the nearest thing. A band
            built from a job that is not the job is worse than no band.
          </p>
          {canDisagree(data) && (
            <div className="actions not-priced__actions">
              <button type="button" className="btn btn--warn" onClick={() => setDisagreeing(true)}>
                {formWords('answer').button}
              </button>
            </div>
          )}
        </section>
        {canDisagree(data) && (
          <BenchmarkFeedback
            jdId={jdId}
            bench={data}
            open={disagreeing}
            suggested={suggested}
            onClose={() => { setDisagreeing(false); setSuggested('') }}
            onKept={() => { setDisagreeing(false); setSuggested(''); reload() }}
          />
        )}
        {/*
          **The explanation was written and then not shown.**

          This page said a role did not match, counted what was set aside,
          and named the survey it came from. The step by step account of why
          was built, saved and left off, so the one case where a person most
          needs to know what happened was the one case that did not tell
          them. They were left with a refusal and no argument.

          The two panels below are the ones that answer "why not" and "what
          came nearest", and they come before the provenance because that is
          the order the questions arrive in.
        */}
        <Closest
          rejected={data.rejected}
          onSuggest={(code) => { setSuggested(code); setDisagreeing(true) }}
        />
        <TheReasoning data={data} />
        <Excluded excluded={data.excluded} country={data.country} of={data.offered?.of} />
        <Evidence bench={data} />
      </section>
    )
  }

  return (
    <section className="screen">
      {header}

      <div className="tabs" role="tablist" aria-label="This benchmark">
        <button
          type="button" role="tab" aria-selected={showing === 'result'}
          className={`tab ${showing === 'result' ? 'tab--on' : ''}`}
          onClick={() => setView('result')}
        >
          Result
        </button>
        <button
          type="button" role="tab" aria-selected={showing === 'matches'}
          className={`tab ${showing === 'matches' ? 'tab--on' : ''}`}
          onClick={() => setView('matches')}
        >
          Matches{shortlist.length > 0 ? ` (${chosen.length} of ${shortlist.length})` : ''}
        </button>
      </div>

      {showing === 'result' ? (
        <>
          {data.band ? (
            <Result
              bench={data}
              band={data.band}
              chosen={shortlist.filter((c) => chosen.includes(c.code))}
              jdId={jdId}
              onAdjust={() => setView('matches')}
              onSaved={reload}
              onDisagree={canDisagree(data) ? () => setDisagreeing(true) : undefined}
            />
          ) : (
            <section className="card card--pad">
              <h2>Nothing is ticked, so there is no band</h2>
              <p className="subline prose">Choose at least one survey job on the Matches tab.</p>
            </section>
          )}
          {canDisagree(data) && (
            <BenchmarkFeedback
              jdId={jdId}
              bench={data}
              open={disagreeing}
              onClose={() => setDisagreeing(false)}
              onKept={() => { setDisagreeing(false); setTicked(null); setLeaning({}); reload() }}
            />
          )}
          <TheReasoning data={data} />
          <Excluded excluded={data.excluded} country={data.country} of={data.offered?.of} />
          <Evidence bench={data} />
        </>
      ) : (
        <>
          {profile && <Profile profile={profile} />}
          <Matches
            shortlist={shortlist}
            chosen={chosen}
            emphasisOf={emphasisOf}
            currency={data.currency}
            /*
             * Picking one cut replaces whatever was chosen from that same
             * survey, and leaves every other survey's choice alone. That is
             * the one-per-provider rule as a gesture rather than an error
             * message.
             */
            onPick={(setName, code) => setTicked([
              ...chosen.filter((c) => {
                const cut = shortlist.find((x) => x.code === c)
                return (cut?.provider?.trim() || 'This survey') !== setName
              }),
              code,
            ])}
            onLean={(code, next) => setLeaning({ ...leaning, [code]: next })}
          />
          <section className="card card--pad actions-bar">
            <p className="subline">
              {chosen.length === 0
                ? 'Nothing chosen yet. Pick one survey job from each survey below.'
                : `Matched to ${chosen.length} ${chosen.length === 1 ? 'survey job' : 'survey jobs'}, from ${sources(shortlist.filter((c) => chosen.includes(c.code)))}.`}
            </p>
            <div className="actions">
              <button
                type="button" className="btn btn--primary" onClick={run}
                disabled={saving || chosen.length === 0}
              >
                {saving ? 'Working it out' : 'Run benchmark'}
              </button>
              {changed && (
                <button type="button" className="btn" onClick={() => { setTicked(null); setLeaning({}) }}>
                  Undo changes
                </button>
              )}
            </div>
            {saveError && <p className="field__error" role="alert">{saveError}</p>}
          </section>
          <Excluded excluded={data.excluded} country={data.country} of={data.offered?.of} />
        </>
      )}
    </section>
  )
}

/* ---------- the role being matched ---------- */

/**
 * What is being matched, beside what it is being matched to.
 *
 * Sending a person to another tab to remember what the role said is how a
 * match gets confirmed without being read.
 */
function Profile({ profile }: { profile: NonNullable<Benchmark['profile']> }) {
  /*
   * Closed to begin with.
   *
   * This role has twenty-five responsibilities, and open by default they push
   * the thing a person came to do, choose a match, most of a screen down. The
   * description is something to consult when a match is arguable, not
   * something to read again every time.
   *
   * The page heading above already names the role, its family, its level and
   * where it is, so a closed panel is not a page with nothing on it.
   */
  const [open, setOpen] = useState(false)

  return (
    <section className="card card--pad">
      <div className="profile__head">
        <h3>
          The role being benchmarked{' '}
          <span className="hint">
            v{profile.version} · {profile.status} · updated {new Date(profile.updated).toLocaleDateString()}
          </span>
        </h3>
        <button
          type="button"
          className="btn"
          aria-expanded={open}
          onClick={() => setOpen(!open)}
        >
          {open ? 'Hide description' : 'Show description'}
        </button>
      </div>

      {!open ? null : (
      <>
      {profile.summary && <p className="prose">{profile.summary}</p>}

      {profile.levelScope && (
        <div className="scope">
          <div className="scope__label">Level scope used for matching</div>
          <p className="prose">{profile.levelScope}</p>
        </div>
      )}

      <div className="two-up">
        <div>
          {profile.responsibilities.length > 0 && (
            <>
              <h4>Responsibilities</h4>
              <ul className="prose">
                {profile.responsibilities.map((line) => <li key={line}>{line}</li>)}
              </ul>
            </>
          )}
        </div>
        <div>
          {profile.skills.length > 0 && (
            <>
              <h4>Required skills</h4>
              <div className="chips">
                {profile.skills.map((skill) => <span className="chip" key={skill}>{skill}</span>)}
              </div>
            </>
          )}
          {profile.quals.length > 0 && (
            <>
              <h4>Qualifications</h4>
              <ul className="prose">
                {profile.quals.map((line) => <li key={line}>{line}</li>)}
              </ul>
            </>
          )}
        </div>
      </div>
      </>
      )}
    </section>
  )
}

/* ---------- the shortlist ---------- */

function Matches({ shortlist, chosen, emphasisOf, currency, onPick, onLean }: {
  shortlist: BenchmarkCut[]
  chosen: string[]
  emphasisOf: (cut: BenchmarkCut) => number
  currency: string | null
  /** Picking one cut replaces whatever was chosen from that same survey. */
  onPick: (setName: string, code: string) => void
  onLean: (code: string, next: number) => void
}) {
  const [openCode, setOpenCode] = useState<string | null>(null)

  if (shortlist.length === 0) {
    return (
      <section className="card card--pad">
        <h3>Suggested market matches</h3>
        <p className="subline prose">Nothing in this survey came close enough to offer.</p>
      </section>
    )
  }

  /*
   * One list per provider, because a provider is the unit a match is made
   * against: one benchmark code per survey, then a blend across surveys.
   *
   * So the choice inside a set is a radio and not a tick. Two codes from one
   * survey is not a blend, it is that survey's view counted twice, and it
   * quietly doubles the weight of whichever provider a role happened to match
   * twice in.
   */
  const bySet = new Map<string, BenchmarkCut[]>()
  for (const cut of shortlist) {
    const name = cut.provider?.trim() || 'This survey'
    bySet.set(name, [...(bySet.get(name) ?? []), cut])
  }
  const sets = [...bySet.entries()]
  const weighted = canWeigh(shortlist)

  return (
    <>
      {sets.map(([setName, cuts]) => (
        <section className="card card--flush" key={setName}>
          <h3 className="card__head">
            {setName}
            <span className="hint">
              {' \u00b7 choose one'}{sets.length > 1 ? ' from this survey' : ''}
            </span>
          </h3>
          <ul className="matches">
            {cuts.map((cut) => {
              const on = chosen.includes(cut.code)
              const open = openCode === cut.code
              return (
            <li className={`match ${on ? 'match--on' : ''}`} key={cut.code}>
              {/*
                * A radio, because the choice inside one survey is which cut,
                * not how many. A tick invited two, and two codes from one
                * survey is that survey counted twice.
                */}
              <input
                className="match__tick"
                type="radio"
                name={`cut-${setName}`}
                checked={on}
                aria-label={`Match to ${cut.title}`}
                onChange={() => onPick(setName, cut.code)}
              />
              <div className="match__what">
                <div className="match__title">
                  {cut.title}{' '}
                  <span className="hint">{[cut.provider, cut.year].filter(Boolean).join(' ')}</span>
                </div>
                <div className="match__meta">
                  {[cut.location, cut.p50 === null ? '' : `median ${money(cut.p50, currency)}`]
                    .filter(Boolean).join(' · ')}
                </div>
                <div className="match__meta">
                  <b>{cut.incumbents.toLocaleString()}</b> incumbents{'  '}
                  <b>{cut.orgs.toLocaleString()}</b> orgs participated
                  {' · '}{cut.code}
                </div>
                {/*
                  * The relationship in one line, for somebody who reads
                  * nothing else on the card. Same three facts in the same
                  * order every time, because a line that changes shape
                  * between two matches cannot be compared at a glance, which
                  * is the only reason it is here.
                  */}
                {cut.relation && <div className="match__relation">{cut.relation}</div>}
                {/*
                  * The reasoning is the screen, not a disclosure under it.
                  *
                  * It was behind a link because the list was the whole
                  * catalogue and opening every row was absurd. The list is
                  * now the answer and a few runners-up, each with a reason
                  * somebody wrote about that row, and the question a person
                  * came here to settle is exactly "why that one". Making
                  * them click to find out was hiding the answer behind the
                  * evidence for it.
                  *
                  * Still collapsible on a benchmark saved before this, where
                  * a row may carry a long generated sentence or nothing.
                  */}
                {/*
                  * What the score is worth, beside the reasoning rather than
                  * beside the number. The score sits in a narrow rail and a
                  * sentence there wraps to five lines of soup.
                  */}
                {howSure(cut.score).says && (
                  <div className="match__sureSays">{howSure(cut.score).says}</div>
                )}
                {/*
                  * One link opens both the provider's own words and the
                  * named reasons, because they are read together: the
                  * description is what the reasons are arguing about.
                  *
                  * Open by default would make a shortlist of six matches
                  * several screens long, which is the thing that made the
                  * old whole-catalogue list unusable.
                  */}
                <button
                  type="button" className="linkish small"
                  aria-expanded={open}
                  onClick={() => setOpenCode(open ? null : cut.code)}
                >
                  {open ? 'Hide survey description & rationale' : 'Survey description & rationale'}
                </button>

                {open && (
                  <>
                    <SurveyDescription cut={cut} />
                    <WhyThisJob cut={cut} />
                  </>
                )}
              </div>

              {/*
                * A bar as well as a number, because a shortlist is scanned,
                * and a word as well as both, because the number on its own
                * misleads. The AI parks at 72% when it is unsure, and a bar
                * three quarters full reads as a good match. See `howSure`.
                */}
              <div className="match__score">
                <div className="match__scoreLabel">
                  Match {Math.round((cut.score ?? 0) * 100)}%
                  <span className={`sure sure--${howSure(cut.score).weight}`}>{howSure(cut.score).label}</span>
                </div>
                <div className="meter" aria-hidden="true">
                  <div className="meter__fill" style={{ width: `${Math.round((cut.score ?? 0) * 100)}%` }} />
                </div>
              </div>

              {/*
                * Weighting scales one provider against another, so it only
                * appears when there is another provider. With one survey
                * there is nothing to weigh against and a control sitting
                * there doing nothing teaches a person it does something.
                */}
              {weighted && (
              <div className="match__lean">
                <span className="match__leanLabel">Weight</span>
                <button
                  type="button" className="stepper" disabled={!on || emphasisOf(cut) <= 1}
                  aria-label={`Count ${cut.title} less`}
                  onClick={() => onLean(cut.code, Math.max(1, emphasisOf(cut) - 1))}
                >
                  &minus;
                </button>
                <span className="match__leanValue num">{emphasisOf(cut)}</span>
                <button
                  type="button" className="stepper" disabled={!on || emphasisOf(cut) >= 5}
                  aria-label={`Count ${cut.title} more`}
                  onClick={() => onLean(cut.code, Math.min(5, emphasisOf(cut) + 1))}
                >
                  +
                </button>
              </div>
              )}
            </li>
              )
            })}
          </ul>
        </section>
      ))}
    </>
  )
}

/**
 * The survey jobs that came nearest to a role nothing matched.
 *
 * **"Nothing matched" with nothing beside it reads as a refusal.** A person
 * who knows these roles wants to see what was considered, so they can say
 * "that one is actually right" or "no, I see why not". Naming the closest
 * job is not matching to it: each line says the part that is the same and
 * the part that is not, which is what tells somebody which survey they are
 * missing.
 *
 * Nothing drawn where the matcher named none, because a heading over an
 * empty list says the question was answered when it was not.
 *
 * `onSuggest` opens the Disagree card with that job filled in (STE-1063). It
 * is a suggestion to the matcher, which may still turn it down and say why.
 */
function Closest({ rejected, onSuggest }: {
  rejected: Benchmark['rejected']
  onSuggest?: (code: string) => void
}) {
  const near = closestOf({ rejected })
  if (near.length === 0) return null
  return (
    <section className="card card--pad">
      <h3>What came closest, and why none of them is this job</h3>
      <dl className="why__list">
        {near.map((r) => (
          <div className="why__row" key={r.code}>
            <dt className="why__label">{r.code}</dt>
            <dd className="why__text prose">
              {plain(r.reason)}
              {onSuggest && (
                <span className="closest__suggest">
                  <button
                    type="button" className="btn--link"
                    aria-label={`Suggest ${r.code}`}
                    onClick={() => onSuggest(r.code)}
                  >
                    Suggest this job
                  </button>
                </span>
              )}
            </dd>
          </div>
        ))}
      </dl>
    </section>
  )
}

/**
 * How the answer was reached, under the steps the matcher works in.
 *
 * It used to be one paragraph of everything. The paragraph is honest and it
 * is a wall: the screenshot that prompted this ran to twenty lines covering
 * the classification, the breakdown, three searches, the ladder, what was
 * ruled out and what did not match, and a reader looking for one of those had
 * to find it.
 *
 * **The headings are the matcher's own steps, not headings invented for a
 * screen.** Its instruction tells it to work in this order, so naming them
 * here lets a reader follow the same path and disagree at the step they
 * disagree with rather than with the whole thing.
 *
 * The paragraph is still kept and still shown where the steps are missing,
 * which is every benchmark saved before this. An old answer is still the
 * answer somebody is looking at.
 */
function TheReasoning({ data }: { data: Benchmark }) {
  const how = data.how
  if (!how && !data.reasoning) return null

  return (
    <section className="card card--pad">
      <h3>The reasoning</h3>
      {how ? (
        <dl className="why__list">
          {HOW_ORDER.map(({ key, label }) => {
            const text = (how[key] ?? '').trim()
            if (!text) return null
            return (
              <div className="why__row" key={key}>
                <dt className="why__label">{label}</dt>
                <dd className="why__text prose">{plain(text)}</dd>
              </div>
            )
          })}
        </dl>
      ) : (
        <>
          <p className="prose">{plain(data.reasoning)}</p>
          {data.managesPeopleReason && (
            <p className="subline prose">
              Read as {data.managesPeople ? 'a people manager' : 'an individual contributor'}: {plain(data.managesPeopleReason)}
            </p>
          )}
        </>
      )}
    </section>
  )
}

/**
 * The provider's own words for this survey job, where the file carries them.
 *
 * **Shown exactly as published, or not at all.** A benchmark job's definition
 * is the thing the pay figures were collected against, so the provider's
 * wording is closer to the truth than anything we could write. Writing one
 * ourselves was proposed and rejected: the match would then be against our
 * reading of a Mercer job rather than against the Mercer job, and nobody
 * could see where an error entered.
 *
 * Most Mercer exports carry no description at all. The column that looks like
 * one holds "in whole numbers", which describes the number format. So the
 * honest answer here is usually that the provider published none, and saying
 * so is better than an empty panel that reads like something failed to load.
 */
function SurveyDescription({ cut }: { cut: BenchmarkCut }) {
  const text = (cut.surveyDescription ?? '').trim()
  return (
    <div className="surveyDesc">
      <div className="surveyDesc__head">
        <span className="surveyDesc__label">Survey description</span>
        <span className="surveyDesc__source">
          {[cut.provider, cut.code].filter(Boolean).join(' · ')}
        </span>
      </div>
      {text ? (
        <div className="surveyDesc__body prose">{plain(text)}</div>
      ) : (
        <p className="surveyDesc__none">
          {cut.provider || 'This provider'} publishes no written description for this job, so
          there is nothing to show here. What the job covers is read from its code, its title
          and the level the provider assigns, which is what the reasons below compare against.
        </p>
      )}
    </div>
  )
}

/**
 * Why this job was selected, under the headings a person asks it in.
 *
 * One paragraph used to answer all seven questions at once, which is honest
 * and impossible to scan. A compensation person comparing three matches can
 * now read down one heading instead of holding three paragraphs in their head.
 *
 * Only the first two are the matcher's judgement. The rest are worked out
 * from the survey row, so they can be checked against it. `why.ts` on the
 * server says which is which, and why no line here states a pay figure or a
 * percentage that did not come from the survey.
 *
 * A benchmark saved before this carries one paragraph and no named reasons.
 * It is shown as it is rather than left blank, because an old answer is still
 * the answer somebody is looking at.
 */
function WhyThisJob({ cut }: { cut: BenchmarkCut }) {
  if (!cut.why) {
    if (!cut.reason) return null
    return (
      <div className="why">
        <div className="why__head">Why this job was selected</div>
        <div className="why__oneLump prose">{plain(cut.reason)}</div>
      </div>
    )
  }

  const why = cut.why
  return (
    <div className="why">
      <div className="why__head">Why this job was selected</div>
      <dl className="why__list">
        {WHY_ORDER.map(({ key, label }) => {
          const text = (why[key] ?? '').trim()
          if (!text) return null
          return (
            <div className="why__row" key={key}>
              <dt className="why__label">{label}</dt>
              <dd className="why__text prose">{plain(text)}</dd>
            </div>
          )
        })}
      </dl>
    </div>
  )
}

/* ---------- the answer ---------- */

function Result({ bench, band, chosen, onAdjust, jdId, onSaved, onDisagree }: {
  bench: Benchmark; band: MarketBand; chosen: BenchmarkCut[]
  onAdjust: () => void
  jdId: string
  onSaved: () => void
  /** Opens the Disagree with this match form. Absent where there is nothing to disagree with. */
  onDisagree?: () => void
}) {
  const years = [...new Set(chosen.map((c) => c.year).filter(Boolean))].sort()
  const providers = [...new Set(chosen.map((c) => c.provider).filter(Boolean))]
  // What the band actually draws on, which is what decides whether there is
  // anything to weigh.
  const weighted = canWeigh(chosen)
  const avgMatch = chosen.length === 0
    ? 0
    : Math.round((chosen.reduce((t, c) => t + (c.score ?? 0), 0) / chosen.length) * 100)

  const [name, setName] = useState(bench.name ?? `${bench.jdTitle || 'Benchmark'} \u2014 ${monthOf(bench.runAt)}`)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)

  async function save() {
    setSaving(true)
    setSaveError(null)
    try {
      const weights: Record<string, number> = {}
      for (const c of chosen) weights[c.code] = c.emphasis ?? 1
      await api.saveBenchmarkCuts(jdId, chosen.map((c) => c.code), weights, name.trim())
      onSaved()
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'That did not save.')
    } finally {
      setSaving(false)
    }
  }

  /*
   * Everything behind the number, as a document somebody can send.
   *
   * The band, the cuts it came from with their own figures, what was left out
   * and the reasoning. A person asked in a meeting where a figure came from
   * should be able to send the answer rather than describe it.
   *
   * **It used to write a `.json` file.** JSON is a format for programs, and
   * what a compensation professional needs is something to attach to an email.
   * The layout lives in `benchmark-pdf.ts`, which also loads the PDF library
   * on demand so no other screen carries it.
   */
  const [exporting, setExporting] = useState(false)
  const [exportError, setExportError] = useState<string | null>(null)

  async function exportPdf() {
    setExporting(true)
    setExportError(null)
    try {
      const { documentFor, renderPdf } = await import('../lib/benchmark-pdf')
      await renderPdf(
        documentFor({ bench, name, band, chosen, currency: bench.currency }),
        `${slug(name)}.pdf`,
      )
    } catch (err) {
      setExportError(err instanceof Error ? err.message : 'That did not export.')
    } finally {
      setExporting(false)
    }
  }

  return (
    <>
      <section className="card card--pad">
        <div className="result__top">
          <div className="result__what">
            <div className="result__kicker">Result</div>
            <h2 className="result__title">{bench.jdTitle || 'This role'}</h2>
            <p className="subline">
              {[
                providers.length > 0 ? providers.join(', ') : 'this survey',
                `${chosen.length} ${chosen.length === 1 ? 'cut' : 'cuts'}`,
                years.length > 0 ? `survey ${years.length === 1 ? 'year' : 'years'} ${years.join('/')}` : '',
              ].filter(Boolean).join(' \u00b7 ')}
            </p>
          </div>

          {/*
            * No compa-ratio, no market position, no confidence.
            *
            * A compa-ratio is a role's own midpoint over the market median,
            * and job descriptions here do not carry a pay range, so it read
            * "Not set" on every role and market position said the same thing
            * again in other words. Confidence went with them: the sample
            * behind the answer is in the table below, cut by cut, where it
            * can be checked rather than flattened into one word.
            *
            * **The band is the answer.** What is left says what the market
            * pays and where every figure came from.
            */}
          <div className="result__stats">
            <div className="figure">
              <div className="figure__label">Market median</div>
              <div className="figure__value">{money(band.p50, bench.currency)}</div>
              <div className="figure__hint hint">
                across {chosen.length} {chosen.length === 1 ? 'cut' : 'cuts'}
                {avgMatch > 0 ? `, average match ${avgMatch}%` : ''}
              </div>
            </div>
          </div>
        </div>

        <Bands band={band} profile={bench.profile ?? null} currency={bench.currency} cuts={chosen.length} />
      </section>

      <section className="card card--flush">
        <h3 className="card__head">Where each figure comes from</h3>
        <table className="table">
          <thead>
            <tr>
              <th scope="col">Source</th>
              <th scope="col">Year</th>
              <th scope="col">Survey job</th>
              <th scope="col">Location</th>
              {/*
                * Only the percentiles this survey reports. A column of dashes
                * headed 90th is a promise the data cannot keep.
                */}
              {band.p25 !== null && <th scope="col">P25</th>}
              <th scope="col">P50</th>
              {band.p75 !== null && <th scope="col">P75</th>}
              {band.p90 !== null && <th scope="col">P90</th>}
              {/*
                * Only when the band draws on more than one provider. A single
                * cut has nothing to be weighted against, and the column read
                * "x1" on the one row it had.
                */}
              {weighted && <th scope="col">Weight</th>}
              <th scope="col">Match</th>
            </tr>
          </thead>
          <tbody>
            {chosen.map((cut) => (
              <tr key={cut.code}>
                <td className="strong">{cut.provider || 'not recorded'}</td>
                <td className="dim">{cut.year || ''}</td>
                <td>{cut.title} <span className="hint">{cut.code}</span></td>
                <td>{cut.location || 'United States'}</td>
                {band.p25 !== null && <td className="num">{money(cut.p25, bench.currency)}</td>}
                <td className="num strong">{money(cut.p50, bench.currency)}</td>
                {band.p75 !== null && <td className="num">{money(cut.p75, bench.currency)}</td>}
                {band.p90 !== null && <td className="num">{money(cut.p90, bench.currency)}</td>}
                {weighted && <td className="num">&times;{cut.emphasis ?? 1}</td>}
                <td className="num">{Math.round((cut.score ?? 0) * 100)}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section className="card card--pad save-bar">
        <label className="field save-bar__name">
          <span className="sr-only">What to call this band</span>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="What to call this band"
          />
        </label>
        <button type="button" className="btn btn--primary" onClick={save} disabled={saving || name.trim() === ''}>
          {saving ? 'Saving' : 'Save benchmark'}
        </button>
        {/* The thing they want, not the format it arrives in. Nobody wants JSON. */}
        <button type="button" className="btn" onClick={exportPdf} disabled={exporting}>
          {exporting ? 'Exporting\u2026' : 'Export'}
        </button>
        <button type="button" className="linkish" onClick={onAdjust}>Adjust matches</button>
        {onDisagree && (
          <button type="button" className="btn btn--warn" onClick={onDisagree}>Disagree with this match</button>
        )}
        {saveError && <p className="field__error" role="alert">{saveError}</p>}
        {/* An export that failed is said out loud. A button that does nothing
            when pressed is the worst of the three possible outcomes. */}
        {exportError && <p className="field__error" role="alert">{exportError}</p>}
      </section>
    </>
  )
}

/** The month a run happened, for the name a band is offered. */
const monthOf = (iso: string): string => {
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? '' : `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
}

/** A file name a person can find again. */
const slug = (s: string): string =>
  s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 80) || 'benchmark'

/**
 * The market band and this role's own range, drawn on one scale.
 *
 * Two bars against different scales would be two pictures, and the whole
 * question is where one sits against the other. So both are measured from the
 * same low to the same high, and a reader can see the answer without reading a
 * single number.
 *
 * **Only the percentiles the survey reported.** A missing one used to be
 * filled in from its neighbour, so a file with no 90th percentile column
 * printed its 75th under a heading of 90TH. That is a figure somebody puts in
 * a pay decision and it was invented here.
 */
function Bands({ band, profile, currency, cuts }: {
  band: MarketBand
  profile: Benchmark['profile']
  currency: string | null
  /** How many cuts the band draws on. One cut is not a blend. */
  cuts: number
}) {
  const shown = ([
    ['P25', band.p25], ['P50', band.p50], ['P75', band.p75], ['P90', band.p90],
  ] as const).filter(([, value]) => value !== null) as [string, number][]

  // Pay is stored in thousands on a job description and in whole currency in a
  // survey. Getting that wrong made a compa-ratio a thousand times out.
  const own = profile && (profile.payMin > 0 || profile.payMax > 0)
    ? { min: profile.payMin * 1000, max: profile.payMax * 1000 }
    : null

  // The scale lives in `scale.ts`, where it can be tested without a browser.
  const { low, high, at } = scaleFor(shown.map(([, v]) => v), own ? [own.min, own.max] : [])
  // Nothing is drawn where every figure lands on the same point: a single bar
  // of full width says a spread that is not there.
  const span = high - low

  const lowest = shown[0]![1]
  const highest = shown[shown.length - 1]![1]

  return (
    <div className="bands">
      <div className="bands__row">
        {/*
          * "Blended" only when something was blended, and "band" only when
          * there is a spread to be a band of. This read "Blended market
          * band" above a single median from a single cut, which is two
          * claims and neither was true.
          */}
        <div className="bands__label">
          {cuts > 1 ? 'Blended market ' : 'Market '}
          {shown.length > 1
            ? `band, ${shown[0]![0]} to ${shown[shown.length - 1]![0]}`
            : 'median'}
        </div>
        <div className="bands__figure num">
          {shown.length > 1
            ? `${money(lowest, currency)} to ${money(highest, currency)}`
            : money(band.p50, currency)}
        </div>
      </div>
      {/*
        * Two tones, because a band has a middle. The pale fill is the whole
        * reported spread and the solid one is the half a compensation person
        * argues about, from the median up to the 75th. Where the survey
        * reports neither, nothing is drawn rather than a made-up middle.
        */}
      <div className="bar" aria-hidden="true">
        {span > 0 && (
          <div
            className="bar__fill"
            style={{ left: `${at(lowest)}%`, width: `${Math.max(at(highest) - at(lowest), 0.6)}%` }}
          />
        )}
        {span > 0 && band.p75 !== null && (
          <div
            className="bar__fill bar__fill--mid"
            style={{ left: `${at(band.p50)}%`, width: `${Math.max(at(band.p75) - at(band.p50), 0.6)}%` }}
          />
        )}
        <div className="bar__mark" style={{ left: `${at(band.p50)}%` }} />
      </div>
      <div className="pcts">
        {shown.map(([label, value]) => (
          <span className={`pct ${label === 'P50' ? 'pct--on' : ''}`} key={label}>
            {label} <strong>{money(value, currency)}</strong>
          </span>
        ))}
      </div>
      {shown.length < 4 && (
        <p className="hint">
          {missing(shown.map(([label]) => label))} not reported by this survey, so
          {' '}{4 - shown.length === 1 ? 'it is' : 'they are'} not shown rather than filled in.
        </p>
      )}

      {own && (
        <>
          <div className="bands__row bands__row--own">
            <div className="bands__label">This role's current range</div>
            <div className="bands__figure num">
              {money(own.min, currency)} to {money(own.max, currency)}
              <span className="subline"> · midpoint {money((own.min + own.max) / 2, currency)}</span>
            </div>
          </div>
          <div className="bar" aria-hidden="true">
            {span > 0 && (
              <div
                className="bar__fill bar__fill--own"
                style={{ left: `${at(own.min)}%`, width: `${Math.max(at(own.max) - at(own.min), 0.6)}%` }}
              />
            )}
            <div className="bar__mark bar__mark--own" style={{ left: `${at((own.min + own.max) / 2)}%` }} />
          </div>
        </>
      )}
    </div>
  )
}

/** Names the percentiles the survey left out, in the order a reader expects. */
const missing = (shown: string[]): string => {
  const gone = ['P25', 'P50', 'P75', 'P90'].filter((p) => !shown.includes(p))
  if (gone.length === 1) return `The ${gone[0]} percentile is`
  return `The ${gone.slice(0, -1).join(', ')} and ${gone[gone.length - 1]} percentiles are`
}

/* ---------- what was left out, and where it came from ---------- */

/** Do not drop this panel. It is what makes a number defensible. */
function Excluded({ excluded, country, of }: {
  excluded: Benchmark['excluded']
  country: string
  /** How many jobs the survey holds, where the answer recorded it. */
  of?: number
}) {
  /*
   * A row with a count stands for that many jobs; a row without one is a
   * single job. Both shapes are in the database: a saved answer from before
   * the change lists every job, and a newer one carries one row per reason,
   * because a large survey listed one job at a time weighs more than a
   * record may hold.
   */
  const grouped = new Map<string, number>()
  for (const item of excluded) {
    const n = 'count' in item ? item.count : 1
    grouped.set(item.reason, (grouped.get(item.reason) ?? 0) + n)
  }
  let total = 0
  for (const n of grouped.values()) total += n

  return (
    <section className="card card--pad">
      {/*
        **Written for somebody defending this number in a meeting.**

        It said "What was left out" over "Narrowed to cuts covering United
        States", which is our words for our rules. A cut is a word this
        product invented; what a reader has is a survey full of jobs, most
        of which could not be used for their role, and they need to be able
        to say why out loud.
      */}
      <h3>Survey jobs set aside before this role was matched</h3>
      <p className="subline">
        {country
          ? `Only jobs covering ${country} were considered.`
          : 'Every country in the survey was considered, because this role\'s location did not name one.'}
      </p>
      {grouped.size === 0 ? (
        <p className="subline">Every job in the survey could be used.</p>
      ) : (
        /*
         * A count and a reason, in two columns rather than run together in a
         * sentence. It was "51 cuts, too thin to publish", which is a list
         * pretending to be prose.
         */
        <>
        {/*
          * **What this counts, said plainly.**
          *
          * It read "3 of this survey's jobs could not be used, and here is
          * why. Most surveys hold far more jobs than any one company can be
          * compared against." Two things were wrong with that. The second
          * sentence describes thousands being set aside and sat next to the
          * number 3, and the whole panel reads as though it were about this
          * role when every role in a survey gets the same list: checked
          * across twelve saved benchmarks, all twelve named the same three
          * rows for the same reason.
          *
          * These are rows the survey itself cannot support a figure from, so
          * the panel now says so and says the rest were available.
          */}
        {/*
          * **"Every other job in the survey could be used" was not always
          * true.** A role in Mexico against a United States survey had all
          * 23,256 of its jobs set aside, and the sentence sat directly under
          * a count that was the whole survey, promising jobs that did not
          * exist.
          *
          * `of` is how many the survey holds. Where it is absent, which is
          * every answer saved before this, the claim is simply not made.
          */}
        <p className="subline">
          {total.toLocaleString()} {total === 1 ? 'job was' : 'jobs were'} set aside before
          matching, because the survey does not support a figure from
          {' '}{total === 1 ? 'it' : 'them'}.
          {of !== undefined && of > total ? ' Every other job in the survey could be used.' : ''}
          {of !== undefined && of <= total ? ' That is every job in the survey, so there was nothing left to compare this role against.' : ''}
        </p>
        <dl className="reasons">
          {[...grouped.entries()]
            .sort((a, b) => b[1] - a[1])
            .map(([reason, count]) => (
              <div className="reasons__row" key={reason}>
                <dt className="num">{count.toLocaleString()}</dt>
                <dd>{reason}</dd>
              </div>
            ))}
        </dl>
        </>
      )}
    </section>
  )
}

/**
 * What answered, in words rather than in identifiers.
 *
 * This printed "agent RMOBICSVLR alias TQSZC0N4P6" to an HR admin. The panel
 * it sits in already stopped printing a survey id for exactly that reason: it
 * tells a customer nothing and reads like something leaking out of the back of
 * the product. The ids stay on the saved record, where somebody looking into a
 * run can still find them.
 */
const answeredBy = (model: string): string =>
  (/^agent\s/.test(model ?? '') ? 'Stello\u2019s matching agent' : (model || 'not recorded'))

/** Where every number came from. Somebody will be asked. */
const Evidence = ({ bench }: { bench: Benchmark }) => {
  const inBand = (bench.cuts ?? []).filter(isChosen)
  const weighted = canWeigh(inBand)
  const cuts = inBand.length
  return (
  <section className="card card--pad">
    <h3>Where this came from</h3>
    {/*
      * Named, not identified. This printed "Survey sv_0mugtpun873148ce05567",
      * which tells a customer nothing and reads like something leaking out.
      * The file count is here because a survey uploaded in several parts is
      * still one survey, and a person checking a figure wants to know how much
      * of their data stood behind it.
      */}
    <dl className="facts">
      <div><dt>Market data</dt><dd>
        {bench.surveyName || 'not recorded'}
        {bench.surveyFiles && bench.surveyFiles > 1 ? `, across ${bench.surveyFiles} uploads` : ''}
      </dd></div>
      <div><dt>Job description</dt><dd>version {bench.jdVersion}</dd></div>
      <div><dt>Run by</dt><dd>{bench.runBy} on {new Date(bench.runAt).toLocaleString()}</dd></div>
      <div><dt>Matched with</dt><dd>{answeredBy(bench.model)}</dd></div>
      {/*
        * Whose decision this is. A band somebody confirmed is a different
        * thing to defend than one a run produced, and a re-run leaves it
        * alone, so the screen should say which it is.
        */}
      {bench.confirmed && (
        <div><dt>Confirmed by</dt><dd>
          {bench.confirmed.by} on {new Date(bench.confirmed.at).toLocaleString()}
        </dd></div>
      )}
    </dl>
    <p className="prose">
      {/*
        * What this sentence is for: an HR director defending a number needs
        * to know the money did not come from a model. The model chooses
        * which survey job matches. The arithmetic is plain code.
        *
        * So it has to describe the arithmetic that actually happened. On a
        * role matched to one cut there is none: the figure is the survey's
        * own, copied across. Saying it was "worked out" there claims a
        * calculation nobody did, which undermines the one thing the sentence
        * is there to establish.
        */}
      {weighted
        ? 'The band and the weighting were worked out in code, not by the model.'
        : cuts > 1
          ? 'The band was worked out in code, not by the model.'
          : 'The model chose which survey job this is. The figure is the survey\u2019s own, not worked out by anyone.'}
    </p>
  </section>
  )
}

/* ---------- small things ---------- */

/**
 * Whether a cut is in the band.
 *
 * Absent on a benchmark saved before the whole shortlist was kept, where every
 * stored cut was a chosen one, so absent reads as ticked.
 */
const isChosen = (cut: BenchmarkCut): boolean => cut.chosen !== false

/**
 * Whether weighting means anything for these cuts.
 *
 * It scales one provider against another, so with one provider there is
 * nothing to scale against. A weight column reading "x1" all the way down is
 * a control that does nothing, and a control that does nothing teaches a
 * person it does something.
 *
 * Mirrors `canWeigh` in the backend's `surveys.ts`, which is what the save
 * route enforces. Two tabs asking the same question through one function
 * here, rather than each working it out and one of them drifting.
 */
const canWeigh = (cuts: { provider?: string }[]): boolean =>
  new Set(cuts.map((c) => (c.provider ?? '').trim().toLowerCase())).size > 1

/** Who published the cuts behind an answer, named rather than counted. */
const sources = (cuts: BenchmarkCut[]): string => {
  const names = [...new Set(cuts.map((c) => c.provider).filter(Boolean))] as string[]
  if (names.length === 0) return 'this survey'
  return names.join(', ')
}

/**
 * A pay range as a job description stores it: in thousands, or null when the
 * role names no pay.
 *
 * Null rather than the words "no pay range set". Most job descriptions here
 * carry no range, so announcing the absence put "current range no pay range
 * set" in the subline of nearly every role, which reads as something wrong
 * with the benchmark rather than a fact about the job description. A role
 * that states a range still shows it. One that does not says nothing.
 */
const range = (min: number, max: number): string | null => {
  if (!(min > 0) && !(max > 0)) return null
  if (min > 0 && max > 0) return `$${min}k to $${max}k`
  return `$${min > 0 ? min : max}k`
}

export type { BenchmarkCut }
